<template>
  <v-container>
    <p class="step-title">{{ $t('personalDataStep.title') }}</p>
    <HfrCard v-if="userInfo && Object.keys(userInfo).length > 0">
      <div class="field-warning mb-0">{{ $t('global.prefilledDataWarning') }} </div>
    </HfrCard>

    <HfrCard>
      <v-form ref="personalDataForm">
        <v-text-field
          v-model="form.birthFamilyName"
          :label="$t('personalDataStep.birthName')"
          filled>
        </v-text-field>
        <v-select
          v-model="form.gender"
          filled
          :items="genders"
          item-text="name"
          item-value="code"
          :label="$t('global.gender')"
          :rules="[rules.required]"
          class="required" />
        <v-select
          v-model="form.nationality"
          :items="countriesList"
          item-text="name"
          item-value="code"
          :label="$t('personalDataStep.nationality')"
          filled
          class="required"
          :rules="[rules.required]"></v-select>
        <v-select
          v-model="form.language"
          :items="languages"
          item-text="name"
          item-value="code"
          default="defaultLocal"
          filled
          :label="$t('personalDataStep.language')"
          :rules="[rules.required]"
          class="required" />
        <v-select
          v-model="form.maritalStatus"
          :items="civilStates"
          item-text="name"
          item-value="code"
          filled
          :label="$t('personalDataStep.maritalStatus')"
          :rules="[rules.required]"
          class="required" />
        <v-select
          v-model="form.confession"
          :items="confessions"
          item-text="name"
          item-value="code"
          filled
          :label="$t('personalDataStep.confession')"
          :rules="[rules.required]"
          class="required" />
        <p class="field-warning mb-0">{{ $t('personalDataStep.verifyStreet') }}</p>
        <v-text-field
          v-model="form.street"
          :label="$t('global.street')"
          filled
          class="required"
          :rules="[rules.required]">
        </v-text-field>
        <p class="field-warning mb-0">{{ $t('personalDataStep.verifyZip') }}</p>
        <v-text-field
          v-model="form.zip"
          :label="$t('global.zip')"
          filled
          class="required"
          :rules="[rules.required]">
        </v-text-field>
        <p class="field-warning mb-0">{{ $t('personalDataStep.verifyCity') }}</p>
        <v-text-field
          v-model="form.city"
          :label="$t('global.city')"
          filled
          class="required"
          :rules="[rules.required]">
        </v-text-field>
        <v-text-field
          v-model="form.mail"
          :label="$t('global.email')"
          filled
          class="required"
          :rules="[rules.required, rules.email]">
        </v-text-field>
        <phone-field
            v-model="form.mobilePhone"
            :label="$t('global.mobilePhone')"
            required
        ></phone-field>
        <phone-field
            v-model="form.fixPhone"
            :label="$t('global.fixPhone')"
        ></phone-field>
      </v-form>
    </HfrCard>
  </v-container>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import HfrCard from '../ui/HfrCard.vue';
import { emailRule, phoneRule, requiredRule } from '@/helpers/validationRules';
import { sortByName } from '@/helpers/sort';
import PhoneField from "@/components/ui/PhoneField.vue";

var countries = require('i18n-iso-countries');
countries.registerLocale(require('i18n-iso-countries/langs/en.json'));
countries.registerLocale(require('i18n-iso-countries/langs/fr.json'));
countries.registerLocale(require('i18n-iso-countries/langs/de.json'));

export default {
  components: {PhoneField, HfrCard },
  name: 'PersonalDataStep',
  data() {
    const lang = this.$i18n.locale.toUpperCase()
    return {
      form: {
        birthFamilyName: null,
        gender: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'M' : null,
        nationality: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'US' : null,
        language: lang,
        maritalStatus: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'NON' : null,
        confession:
          process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'AUT' : null,
        street:
          process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'Hollywood Bvd' : null,
        zip: process.env.VUE_APP_PREFILL_FORMS === 'true' ? '19111' : null,
        city: process.env.VUE_APP_PREFILL_FORMS === 'true' ? 'LA' : null,
        mail:
          process.env.VUE_APP_PREFILL_FORMS === 'true'
            ? 'dave@grohl.rock'
            : null,
        mobilePhone:
          process.env.VUE_APP_PREFILL_FORMS === 'true' ? '+41/786785853' : null,
        fixPhone:
          process.env.VUE_APP_PREFILL_FORMS === 'true' ? '+41262452454' : null
      },
      rules: {
        required: requiredRule,
        email: (value) => emailRule(value),
        phone: (value) => phoneRule(value)
      },
      stepNumber: 4,
      defaultLocale: lang
    };
  },
  watch: {
    insuranceCardInfo: {
      handler(insuranceCardInfo) {
        if (insuranceCardInfo) {
          this.fillFormData();
        }
      }
    },
    userInfo: {
      handler() {
        this.fillFormDataWithUserInfo();
      }
    },
    formIsValid(isValid) {
      this.setFormValidity(isValid);
    },
    navigateToNextStep: {
      handler(navigateToNextStep) {
        if (this.currentStep === this.stepNumber && navigateToNextStep) {
          this.validatePersonalDataForm();
        }
      }
    }
  },
  methods: {
    ...mapActions('navigation', [
      'setFormValidity',
      'doNextStep',
      'toggleNavigateToNextStep'
    ]),
    ...mapActions('preAdmission', ['storePersonalDataStep']),
    validatePersonalDataForm() {
      if (this.$refs.personalDataForm.validate()) {
        this.storePersonalDataStep(this.form);
        this.doNextStep();
      }
      this.toggleNavigateToNextStep();
    },
    fillFormData() {
      const infos = this.insuranceCardInfo?.data;
      if (infos != null) {
        const baseInformation = infos['CH-Baseinformation'];
        this.form.gender = (baseInformation.identificationData.sex === '1') ? 'M' : 'F';
        this.form.zip = baseInformation.nationalExtension.mailAddress.swissZipCode;
        this.form.street = baseInformation.nationalExtension.mailAddress.street;
        this.form.city = baseInformation.nationalExtension.mailAddress.town;
      }
    },
    fillFormDataWithUserInfo() {
      const lang = this.$i18n.locale.toUpperCase()
      this.form = {
        birthFamilyName: null,
        gender: null,
        nationality: null,
        language: lang,
        maritalStatus:  null,
        confession: null,
        street: null,
        zip: null,
        city:  null,
        mail:null,
        mobilePhone: null,
        fixPhone:null
      };
      const infos = this.userInfo?.patient;
      if (infos != null) {
        this.form.gender = infos.sex;
        this.form.birthFamilyName = infos.birth_name;
        this.form.nationality = infos.nationality;
        this.form.language = infos.contact_language;
        this.form.maritalStatus = infos.civil_state;
        this.form.confession = infos.confession;
        if (infos.emails[0]?.address) {
          this.form.mail = infos.emails[0]?.address
        }
        const address = infos.addresses[0]
        if (address != null) {
          this.form.zip = address.zip_code;
          this.form.street = address.street;
          this.form.city = address.city;
        }


        this.form.mobilePhone = infos.phones[0]?.number;
        this.form.fixPhone = infos.phones[1]?.number;
      } else {
        this.fillFormData();
      }
      this.$refs.personalDataForm.resetValidation();
    },
  },
  computed: {
    ...mapState('navigation', ['currentStep', 'navigateToNextStep']),
    ...mapState('preAdmission', ['civilStates', 'confessions', 'genders', 'languages','userInfo']),
    ...mapState('insuranceCard', ['insuranceCardInfo']),
    countriesList() {
      let switzerland = null;
      let topCountryEntries = [];
      let countryEntries = [];
      Object.entries(countries.getNames(this.defaultLocale.toLowerCase(), { select: 'official' })).forEach(
        (country) => {
          // Build the top list
          if (country[0] === 'CH') {
            switzerland = { code: country[0], name: country[1] };
          }
          if (['DE', 'FR', 'IT', 'ES', 'PT'].includes(country[0])) {
            topCountryEntries.push({ code: country[0], name: country[1] });
          }

          countryEntries.push({ code: country[0], name: country[1] });
        }
      );
      countryEntries.sort(sortByName);
      // Add top list to the countryEntries
      topCountryEntries.sort(sortByName);
      topCountryEntries = [switzerland, ...topCountryEntries];
      topCountryEntries.push({ divider: true });
      countryEntries = topCountryEntries.concat(countryEntries);

      return countryEntries;
    }
  }
};
</script>
